import { Field } from 'formik'
import cloneDeep from 'lodash-es/cloneDeep'
import filter from 'lodash-es/filter'
import map from 'lodash-es/map'
import set from 'lodash-es/set'
import React from 'react'

import {
  Checkbox,
  ConfirmModal,
  Icon,
  Input,
  SortableList,
  useMediaQueryContext,
  useModalContext,
} from '@fourthwall/components'
import { CloseIcon } from '@fourthwall/icons'
import type { Type, VariantTypeOption } from '@fourthwall/services/dist/models/variantType'
import { arrayMove } from '@fourthwall/utils/dist/array'

import { ProductVariant } from '../ProductVariant'
import * as S from './styled'
import type { ProductVariantTypeProps } from './types'

const typesLabelMap: { [key in Type]: string } = {
  COLOR: 'Add different colors',
  SIZE: 'Add different sizes',
  CUSTOM: 'Add custom variants',
  MANUFACTURER_PREVIEW: '',
}

const addInputLabelMap: { [key in Type]: string } = {
  COLOR: 'Add color',
  SIZE: 'Add size',
  CUSTOM: 'Add variant',
  MANUFACTURER_PREVIEW: '',
}

const variesByPriceMap: { [key in Type]: string } = {
  COLOR: 'Different colors have different prices',
  SIZE: 'Different sizes have different prices',
  CUSTOM: 'Different variants have different prices',
  MANUFACTURER_PREVIEW: '',
}

const variesByImageryMap: { [key in Type]: string } = {
  COLOR: 'Different colors have different photography',
  SIZE: 'Different sizes have different photography',
  CUSTOM: 'Different variants have different photography',
  MANUFACTURER_PREVIEW: '',
}

const variesByWeightMap: { [key in Type]: string } = {
  COLOR: 'Different colors have different weights',
  SIZE: 'Different sizes have different weights',
  CUSTOM: 'Different variants have different weights',
  MANUFACTURER_PREVIEW: '',
}

export const ProductVariantType = ({
  index,
  variantType,
  shopWeightUnit,
  allValues,
  errors,
  setValues,
}: ProductVariantTypeProps) => {
  const media = useMediaQueryContext()
  const { open } = useModalContext()
  const { options, type, variesBy } = variantType

  const otherHasVaryingValue = (fieldName: any) =>
    (variantType.variesBy as any)[fieldName] &&
    allValues.some((value) => value !== variantType && (value.variesBy as any)[fieldName])

  const showWeightsInfo = otherHasVaryingValue('weight')

  const getDefaultWeightUnit = () => options[0]?.weight?.unit || shopWeightUnit

  const createVariant = (newVariantName: any) => {
    if (newVariantName) {
      setValues({
        types: set(cloneDeep(allValues), `[${index}].options`, [
          ...allValues[index].options,
          {
            name: newVariantName,
            images: [],
            colorSwatch: type === 'COLOR' ? '#000000' : undefined,
            weight: variantType.variesBy.weight ? { unit: getDefaultWeightUnit() } : undefined,
          },
        ]),
      })
    }
  }

  const removeVariant = (variantIndex: number) => {
    const filteredOptions = filter(
      allValues[index].options,
      (item, optionIndex) => optionIndex !== variantIndex,
    )

    setValues({ types: set(cloneDeep(allValues), `[${index}].options`, filteredOptions) })
  }

  const removeVariantType = () => {
    setValues({ types: allValues.filter((value, valueIndex) => index !== valueIndex) })
  }

  const handleVariantRemove = (variantIndex: number) => {
    const variant = variantType.options[variantIndex]

    if (variant) {
      open(ConfirmModal, {
        title: `Are you sure you want to delete ${variant.name} variant?`,
        confirmLabel: 'Yes, delete',
        cancelLabel: 'No, cancel',
        onConfirm: () => removeVariant(variantIndex),
      })
    }
  }

  const handleTypeRemove = () => {
    open(ConfirmModal, {
      title: `Are you sure you want to delete ${variantType.title} variant type?`,
      text: `This will permanently delete all ${variantType.title} variants`,
      confirmLabel: 'Yes, delete',
      cancelLabel: 'No, cancel',
      onConfirm: removeVariantType,
    })
  }

  const handleSortChange = (oldVariantIndex: number, newVariantIndex: number) => {
    const sortedArray = arrayMove(
      cloneDeep(allValues[index].options),
      oldVariantIndex,
      newVariantIndex,
    )

    setValues({ types: set(cloneDeep(allValues), `[${index}].options`, sortedArray) })
  }

  const handleVariesByWeightChange = (value: boolean) => {
    if (value) {
      const updatedOptions = cloneDeep(allValues[index].options).map((option) =>
        set(option, 'weight.unit', getDefaultWeightUnit()),
      )

      const valuesWithUpdatedWeight = cloneDeep(set(allValues, `[${index}].variesBy.weight`, true))

      setValues({ types: set(valuesWithUpdatedWeight, `[${index}].options`, updatedOptions) })
    }
  }

  const sortableListFooter = (
    <SortableList.FooterItem>
      <S.AddInputContainer data-testid="ProductVariantType.AddInputContainer">
        <Input
          label={addInputLabelMap[type]}
          onValueChange={createVariant}
          size={media.tablet ? 'large' : 'medium'}
        />
      </S.AddInputContainer>
    </SortableList.FooterItem>
  )

  const renderSortableListItem = ({ id }: VariantTypeOption, variantIndex: number) => (
    <SortableList.Item key={variantIndex}>
      <ProductVariant
        variantTypeIndex={index}
        index={variantIndex}
        type={type}
        errors={errors}
        shouldAutofocusName={!id}
        variesByWeight={variesBy.weight}
        showWeightsInfo={showWeightsInfo}
      />
    </SortableList.Item>
  )

  return (
    <S.Container>
      <S.RemoveContainer onClick={handleTypeRemove} data-testid="ProductVariantType.Remove">
        <Icon component={CloseIcon} height={12} />
      </S.RemoveContainer>
      <S.Label>{typesLabelMap[type]}</S.Label>
      <S.InputsContainer>
        <S.InputWrapper>
          <Field
            name={`types[${index}].title`}
            component={Input}
            displayError={false}
            label="Title"
            maxLength={80}
          />
        </S.InputWrapper>
      </S.InputsContainer>
      <S.SortableListContainer>
        <SortableList
          footer={sortableListFooter}
          onRemove={handleVariantRemove}
          onSortChange={handleSortChange}
        >
          {map(options, renderSortableListItem)}
        </SortableList>
      </S.SortableListContainer>
      <S.CheckboxesContainer>
        {type !== 'SIZE' && (
          <S.CheckboxWrapper>
            <Field
              name={`types[${index}].variesBy.price`}
              component={Checkbox}
              label={variesByPriceMap[type]}
              size="medium"
            />
          </S.CheckboxWrapper>
        )}
        <S.CheckboxWrapper>
          <Field
            name={`types[${index}].variesBy.imagery`}
            component={Checkbox}
            label={variesByImageryMap[type]}
            size="medium"
          />
        </S.CheckboxWrapper>
        {(type === 'SIZE' || type === 'CUSTOM') && (
          <S.CheckboxWrapper>
            <Field
              name={`types[${index}].variesBy.weight`}
              component={Checkbox}
              onValueChange={handleVariesByWeightChange}
              label={variesByWeightMap[type]}
              size="medium"
            />
          </S.CheckboxWrapper>
        )}
      </S.CheckboxesContainer>
    </S.Container>
  )
}
